import React from "react";
import { Product } from "../../types/Product";
import { useLocation, useNavigate } from "react-router-dom";
import AddToCartButton from "./add-to-cart";
import BaseApi from "../../services/base-api";
import { useToast } from "../../context/ToastContext";
import { ImageWithFallback } from "../../constants/image-placeholder";
import { Arrow, Chevron, Heart, ImageIcon, Zap } from "../CustomSVG";
import { DARK_BLUE, LIGHT_GRAY, WHITE } from "../../constants/colors";
import { useTranslation } from "react-i18next";
import Price from "../Currency/Price";
import "../../styles/add-to-cart.css";
import ProductRating from "./rating";
import { AlertTriangle, CheckCircle, XCircle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { decodeHTML } from "../../utils/textFormatters";

// Default placeholder image
const DEFAULT_PRODUCT_IMAGE =
  "https://afcogroup.in/wp-content/uploads/2022/11/placeholder-24.png";

const ProductCard = ({
  product,
  canAddToCart = true,
  canAddToFavorite = true,
  canEdit = false,
  canRate = false,
  showRating = true,
  isShadow = true,
  isPopularGift = false,
  onAddToCartClick,
}: {
  product: Product;
  canAddToCart?: boolean;
  canAddToFavorite?: boolean;
  canEdit?: boolean;
  canRate?: boolean;
  showRating?: boolean;
  isShadow?: boolean;
  isPopularGift?: boolean;
  onAddToCartClick?: (product: Product) => void;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { success, err } = useToast();

  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [selectedSize, setSelectedSize] = React.useState("");
  const [selectedAttributes, setSelectedAttributes] = React.useState<
    Record<string, string>
  >({});
  const { user, isAuthenticated } = useAuth();
  const [isFavorite, setIsFavorite] = React.useState(
    location.pathname.includes("/account/favorites") ||
      product.isFavorite ||
      false,
  );

  const [isHovered, setIsHovered] = React.useState(false);
  const [imageError, setImageError] = React.useState(false);
  const { t, i18n } = useTranslation();

  const stockStatuses = [
    {
      value: "in_stock",
      label: t("In Stock"),
      color: "bg-green-100 text-green-800",
      icon: CheckCircle,
    },
    {
      value: "low_stock",
      label: t("Low Stock"),
      color: "bg-yellow-100 text-yellow-800",
      icon: AlertTriangle,
    },
    {
      value: "out_of_stock",
      label: t("Out of Stock"),
      color: "bg-red-100 text-red-800",
      icon: XCircle,
    },
  ];

  const hasVariations = (product?.variations || []).length > 0;
  const useQuickAddModal = typeof onAddToCartClick === "function";

  React.useEffect(() => {
    setSelectedAttributes({});
  }, [product.id]);

  // Get images with fallback to placeholder
  const images = React.useMemo(() => {
    if (product?.images?.length > 0) {
      return product.images;
    }
    return [DEFAULT_PRODUCT_IMAGE];
  }, [product?.images]);

  const nextImage = (e: any) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
    setImageError(false);
  };

  const prevImage = (e: any) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    setImageError(false);
  };

  const goToImage = (index: number, e: any) => {
    e.stopPropagation();
    setCurrentImageIndex(index);
    setImageError(false);
  };

  const handleFavoriteClick = async (e: any, productId: string) => {
    e.stopPropagation();
    const res = await BaseApi.post("/favorites/toggle", { productId });

    if (res.success) {
      setIsFavorite(!isFavorite);
      success(
        `${
          !isFavorite
            ? t("Added to favorites Successfully")
            : t("Removed from favorites Successfully")
        }`,
      );

      // Dispatch wishlist update event
      window.dispatchEvent(
        new CustomEvent("wishlistUpdated", {
          detail: {
            action: !isFavorite ? "add" : "remove",
            productId,
          },
        }),
      );
    } else {
      err(
        `Failed to ${
          !isFavorite ? "Add to" : "Remove from"
        } favorites. Please try again`,
      );
    }
  };

  const handleSizeClick = (size: string, e: any) => {
    e.stopPropagation();
    setSelectedSize(size);
  };

  const handleQuickView = (e: any) => {
    e.stopPropagation();
    navigateToDetails();
  };

  const navigateToDetails = () => {
    navigate(`/product/${product.id}`);
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const getStockStatusColor = (status: string) => {
    return (
      stockStatuses.find((s) => s.value === status)?.color ||
      "bg-gray-100 text-gray-800"
    );
  };

  const variationOptions = React.useMemo(() => {
    const optionsMap: Record<string, Set<string>> = {};
    (product?.variations || []).forEach((variation) => {
      Object.entries(variation.attributes || {}).forEach(([key, value]) => {
        if (!optionsMap[key]) optionsMap[key] = new Set();
        if (value) optionsMap[key].add(value);
      });
    });

    return Object.fromEntries(
      Object.entries(optionsMap).map(([key, set]) => [key, Array.from(set)]),
    );
  }, [product?.variations]);

  const requiresVariationSelection =
    !useQuickAddModal &&
    hasVariations &&
    Object.keys(variationOptions).length > 0;

  const selectedVariation = React.useMemo(() => {
    if (!requiresVariationSelection) return null;

    return (product?.variations || []).find((variation) =>
      Object.entries(variationOptions).every(
        ([key]) =>
          selectedAttributes[key] &&
          variation.attributes?.[key] === selectedAttributes[key],
      ),
    );
  }, [
    product?.variations,
    variationOptions,
    selectedAttributes,
    requiresVariationSelection,
  ]);

  const rawDescription =
    i18n.language === "ar" ? product.description?.ar : product.description?.en;

  const finalDescription = rawDescription ? decodeHTML(rawDescription) : "";

  const handleAttributeClick = (
    type: string,
    value: string,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    setSelectedAttributes((prev) => ({ ...prev, [type]: value }));
  };

  return (
    <div style={{ maxWidth: "24rem" }}>
      <div
        style={
          isShadow
            ? {
                backgroundColor: WHITE,
                borderRadius: "1rem",
                boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                overflow: "hidden",
                position: "relative",
                transition: "all 0.3s",
                cursor: "pointer",
              }
            : {
                backgroundColor: "transparent",
                boxShadow: "transparent",
                // borderRadius: "1rem",
                // overflow: "hidden",
                position: "relative",
                // transition: "all 0.3s",
                cursor: "pointer",
              }
        }
        // style={isShadow ? {
        //   backgroundColor: WHITE,
        //   borderRadius: "1rem",
        //   boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
        //   overflow: "hidden",
        //   position: "relative",
        //   transition: "all 0.3s",
        //   cursor: "pointer",
        // } : {
        //   backgroundColor: 'transparent',
        //   boxShadow: 'transparent',
        //   // borderRadius: "1rem",
        //   // overflow: "hidden",
        //   position: "relative",
        //   // transition: "all 0.3s",
        //   cursor: "pointer"
        // }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow =
            "0 20px 25px -5px rgba(0, 0, 0, 0.1)";
          setIsHovered(true);
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow =
            "0 10px 15px -3px rgba(0, 0, 0, 0.1)";
          setIsHovered(false);
        }}
        onClick={navigateToDetails}
      >
        {/* Badges */}
        <div
          style={{
            position: "absolute",
            top: "-0.7rem",
            left: "1rem",
            right: "1rem",
            zIndex: 10,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div style={{ display: "flex", flexDirection: "row", gap: "0.5rem" }}>
            {product?.country?.title && (
              <div
                style={{
                  marginLeft: "24px",
                  marginTop: "24px",
                  background: "linear-gradient(to right, #000, #000)",
                  color: WHITE,
                  padding: "0.375rem 0.75rem",
                  borderRadius: "9999px",
                  fontSize: "0.75rem",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem",
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                }}
              >
                {product.country?.title}
              </div>
            )}
            {/* ADD THIS GIFT BADGE */}
            {isPopularGift && (
              <div
                style={{
                  marginTop: "24px",
                  background: "linear-gradient(to right, #f093fb, #f5576c)",
                  color: WHITE,
                  padding: "0.375rem 0.75rem",
                  borderRadius: "9999px",
                  fontSize: "0.75rem",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem",
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                }}
              >
                🎁 {t("Popular Gift")}
              </div>
            )}
            {product.badge && (
              <div
                style={{
                  marginTop: "24px",
                  background: "linear-gradient(to right, #9333ea, #ec4899)",
                  color: WHITE,
                  padding: "0.375rem 0.75rem",
                  borderRadius: "9999px",
                  fontSize: "0.75rem",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem",
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                }}
              >
                <Zap />
                {product.badge}
              </div>
            )}
          </div>
          {canAddToFavorite && (
            <button
              onClick={(e) => handleFavoriteClick(e, product.id)}
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                marginTop: "24px",
                backdropFilter: "blur(4px)",
                padding: "0.5rem",
                borderRadius: "9999px",
                boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                border: "none",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = WHITE)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor =
                  "rgba(255, 255, 255, 0.9)")
              }
            >
              <Heart filled={isFavorite} />
            </button>
          )}
        </div>

        {/* Image Carousel */}
        <div
          style={{
            position: "relative",
            aspectRatio: "1",
            overflow: "hidden",
            backgroundColor: imageError ? "#f3f4f6" : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {imageError ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <ImageIcon />
              <span style={{ fontSize: "0.875rem", color: LIGHT_GRAY }}>
                {t("No image available")}
              </span>
            </div>
          ) : (
            <ImageWithFallback
              src={images[currentImageIndex]}
              alt={product.name?.en || "Product"}
              className="w-full h-full object-cover"
              style={{
                transition: "transform 0.7s ease",
                transform: isHovered ? "scale(1.1)" : "scale(1)",
              }}
              onError={() => handleImageError}
            />
          )}

          {/* Image Navigation (only show if multiple images and no error) */}
          {images.length > 1 && !imageError && (
            <>
              <button
                onClick={prevImage}
                style={{
                  position: "absolute",
                  left: "1rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(4px)",
                  padding: "0.625rem",
                  borderRadius: "9999px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.2)",
                  border: "none",
                  cursor: "pointer",
                  opacity: isHovered ? 1 : 0,
                  transition: "all 0.3s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 5,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = WHITE;
                  e.currentTarget.style.transform =
                    "translateY(-50%) scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "rgba(255, 255, 255, 0.95)";
                  e.currentTarget.style.transform = "translateY(-50%) scale(1)";
                }}
              >
                <Chevron isLeft />
              </button>

              <button
                onClick={nextImage}
                style={{
                  position: "absolute",
                  right: "1rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(4px)",
                  padding: "0.625rem",
                  borderRadius: "9999px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.2)",
                  border: "none",
                  cursor: "pointer",
                  opacity: isHovered ? 1 : 0,
                  transition: "all 0.3s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 5,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = WHITE;
                  e.currentTarget.style.transform =
                    "translateY(-50%) scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "rgba(255, 255, 255, 0.95)";
                  e.currentTarget.style.transform = "translateY(-50%) scale(1)";
                }}
              >
                <Chevron />
              </button>
            </>
          )}

          {/* Image Indicators */}
          {images.length > 1 && !imageError && (
            <div
              style={{
                position: "absolute",
                bottom: "1rem",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: "0.375rem",
                zIndex: 5,
              }}
            >
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => goToImage(index, e)}
                  style={{
                    width: index === currentImageIndex ? "2rem" : "0.5rem",
                    height: "0.5rem",
                    backgroundColor: WHITE,
                    opacity: index === currentImageIndex ? 1 : 0.5,
                    borderRadius: "9999px",
                    border: "none",
                    cursor: "pointer",
                    transition: "all 0.3s",
                  }}
                  onMouseEnter={(e) => {
                    if (index !== currentImageIndex)
                      e.currentTarget.style.opacity = "0.75";
                  }}
                  onMouseLeave={(e) => {
                    if (index !== currentImageIndex)
                      e.currentTarget.style.opacity = "0.5";
                  }}
                />
              ))}
            </div>
          )}

          {/* Quick View Button */}
          {!imageError && (
            <div
              style={{
                position: "absolute",
                bottom: "3rem",
                left: "50%",
                transform: `translateX(-50%) translateY(${
                  isHovered ? "0" : "20px"
                })`,
                opacity: isHovered ? 1 : 0,
                transition: "all 0.3s",
                zIndex: 5,
              }}
              className="md:block hidden md:opacity-0 md:hover:opacity-100"
            >
              <button
                onClick={handleQuickView}
                style={{
                  backgroundColor: WHITE,
                  color: DARK_BLUE,
                  padding: "0.75rem 1.5rem",
                  borderRadius: "9999px",
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  border: "none",
                  cursor: "pointer",
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.3)",
                  transition: "all 0.2s",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f3f4f6";
                  e.currentTarget.style.transform = "scale(1.05)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = WHITE;
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                {t("Quick View")}
                <Arrow />
              </button>
            </div>
          )}
        </div>

        {/* Product Info */}
        <div style={{ padding: "1.25rem" }}>
          {/* Product Name */}
          <h3
            style={{
              fontSize: "1.125rem",
              fontWeight: "bold",
              color: DARK_BLUE,
              marginBottom: "0.5rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              minHeight: "3rem",
              WebkitBoxOrient: "vertical",
            }}
          >
            {(i18n.language === "ar" ? product.name?.ar : product.name?.en) ||
              t("Product Name")}
          </h3>

          {/* Description - Hidden on mobile, shown on desktop */}
          {/* <div className="hidden md:block">
            <p
              style={{
                fontSize: "0.875rem",
                color: "#4b5563",
                marginBottom: "1rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {finalDescription || t("No description available")}
            </p>
          </div> */}
          <div
            className="text-gray-600 leading-relaxed overflow-hidden"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
            dangerouslySetInnerHTML={{
              __html: finalDescription || "",
            }}
          />

          {/* Variation Selection */}
          {requiresVariationSelection && (
            <div style={{ marginBottom: "1rem" }}>
              {Object.entries(variationOptions).map(([type, values]) => (
                <div key={type} style={{ marginBottom: "0.75rem" }}>
                  <p
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: "0.5rem",
                    }}
                  >
                    {t("SELECT")} {type.toUpperCase()}
                  </p>
                  <div
                    style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}
                  >
                    {values.map((value) => (
                      <button
                        key={`${type}-${value}`}
                        onClick={(e) => handleAttributeClick(type, value, e)}
                        style={{
                          padding: "0.5rem 1rem",
                          borderRadius: "0.5rem",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          backgroundColor:
                            selectedAttributes[type] === value
                              ? DARK_BLUE
                              : "#f3f4f6",
                          color:
                            selectedAttributes[type] === value
                              ? WHITE
                              : "#374151",
                          border: "none",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          boxShadow:
                            selectedAttributes[type] === value
                              ? "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
                              : "none",
                          transform:
                            selectedAttributes[type] === value
                              ? "scale(1.05)"
                              : "scale(1)",
                        }}
                        onMouseEnter={(e) => {
                          if (selectedAttributes[type] !== value)
                            e.currentTarget.style.backgroundColor = "#e5e7eb";
                        }}
                        onMouseLeave={(e) => {
                          if (selectedAttributes[type] !== value)
                            e.currentTarget.style.backgroundColor = "#f3f4f6";
                        }}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Size Selection (Non-variation products only) */}
          {!useQuickAddModal &&
            !hasVariations &&
            product?.sizes &&
            product.sizes.length > 0 && (
              <div style={{ marginBottom: "1rem" }}>
                <p
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "0.5rem",
                  }}
                >
                  {t("SELECT SIZE")}
                </p>
                <div
                  style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}
                >
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={(e) => handleSizeClick(size, e)}
                      style={{
                        padding: "0.5rem 1rem",
                        borderRadius: "0.5rem",
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        backgroundColor:
                          selectedSize === size ? DARK_BLUE : "#f3f4f6",
                        color: selectedSize === size ? WHITE : "#374151",
                        border: "none",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        boxShadow:
                          selectedSize === size
                            ? "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
                            : "none",
                        transform:
                          selectedSize === size ? "scale(1.05)" : "scale(1)",
                      }}
                      onMouseEnter={(e) => {
                        if (selectedSize !== size)
                          e.currentTarget.style.backgroundColor = "#e5e7eb";
                      }}
                      onMouseLeave={(e) => {
                        if (selectedSize !== size)
                          e.currentTarget.style.backgroundColor = "#f3f4f6";
                      }}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

          {/* Price and Stock */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              marginBottom: "1rem",
              gap: "0.75rem",
            }}
            className="md:flex-row md:items-center md:justify-between md:gap-4"
          >
            <div
              className="text-2xl font-bold text-pink-600"
              style={{
                fontSize: "1.5rem",
                fontWeight: "bold",
              }}
            >
              <Price amount={Number(product?.price)} />
            </div>
            <div className="flex justify-between items-center gap-2">
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${getStockStatusColor(
                  product.stockStatus,
                )}`}
              >
                {
                  stockStatuses.find((s) => s.value === product.stockStatus)
                    ?.label
                }
              </span>

              {isAuthenticated && user?.role !== "customer" && (
                <span className="px-2 py-0.5 text-md font-medium bg-gray-100 text-gray-700 rounded-md">
                  {product.totalStock || 0} left
                </span>
              )}
            </div>
          </div>

          {/* Rating */}
          {showRating && (
            <ProductRating
              product={product}
              mode={canEdit ? "full" : "compact"}
              showReviews={true}
              allowRating={!canEdit || canRate}
              onRatingUpdate={(rating) => {
                console.info("New rating:", rating);
              }}
            />
          )}

          {/* Add to Cart Button */}
          {canAddToCart && (
            <>
              {useQuickAddModal ? (
                <button
                  // disabled={product.stock <= 0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddToCartClick?.(product);
                  }}
                  className="add-to-cart-button w-full mt-3 text-sm md:text-base py-2 md:py-3"
                >
                  <span className="button-content">
                    <span className="button-text">{t("Add to Cart")}</span>
                  </span>
                </button>
              ) : requiresVariationSelection && !selectedVariation ? (
                <button
                  disabled
                  className="add-to-cart-button w-full mt-3 text-sm md:text-base py-2 md:py-3"
                >
                  <span className="button-content">
                    <span className="button-text">{t("Select options")}</span>
                  </span>
                </button>
              ) : (
                <AddToCartButton
                  disabled={(selectedVariation?.stock ?? product.stock) <= 0}
                  productId={product.id}
                  variationId={selectedVariation?.id}
                  selectedSize={selectedSize}
                  onSuccess={() => {
                    console.info("Added to cart!");
                  }}
                  className="w-full mt-3 text-sm md:text-base py-2 md:py-3"
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
